import { Entity } from "@common/types/entity";
import { formatISO } from "date-fns";
import { AccountingReportFilters } from "@components/accounting/types";
import { exportProfitAndLoss } from "@api/accountingReports.api";
import {
    getReportEntityTitle,
    getReportTypeTitle,
    ReportFormat,
} from "@common/helpers/reports";
import {
    AccountingReportType,
    ACCOUNTING_REPORT_LABELS,
} from "@common/types/reports/report-type";
import { saveBlobResponse, useFileDownload } from "@lib/fileDownload";
import { ReportSettingRowVisibility } from "@common/types/reports/report-settings";
import { pickXlsxExportRowVisibility } from "@components/accounting/xlsxExportRowVisibility";

interface DownloadParameters {
    entity: Entity;
    format: ReportFormat;
    startDate?: Date;
    endDate?: Date;
}

export function useProfitLossDownload(
    requestDto: AccountingReportFilters,
    rowVisibility?: ReportSettingRowVisibility,
) {
    return useFileDownload(
        async ({ entity, format, startDate, endDate }: DownloadParameters) => {
            if (!requestDto) {
                return;
            }

            const modifiedRequestDto = {
                ...requestDto,
                format,
            };

            const exportRowVisibility = pickXlsxExportRowVisibility(
                format,
                rowVisibility,
            );

            const response = await exportProfitAndLoss(
                modifiedRequestDto,
                exportRowVisibility,
            );

            if (!response) {
                return;
            }

            const entityTitle = getReportEntityTitle(
                entity.name ?? "",
                requestDto.cycle,
            );
            const reportTypeTitle = getReportTypeTitle(
                ACCOUNTING_REPORT_LABELS[AccountingReportType.PROFIT_LOSS],
                requestDto.cycle,
            );
            const fileName = `${entityTitle} - ${reportTypeTitle} - ${startDate ? formatISO(startDate, { representation: "date" }) : ""} - ${endDate ? formatISO(endDate, { representation: "date" }) : ""}.${format}`;

            saveBlobResponse(response, fileName);
        },
    );
}
